import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CheckboxStateful,
  Label,
  TextBody,
  get,
  useFocus,
  useModalContext,
  useSteppedProcess,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import {
  StepTwoFormValues,
  stepTwoBeneErrors,
} from '../../common/AddBeneficiary/StepTwoAddNewBene'
import { AllocationStepIraValues } from './AllocationStepIra'
import {
  AddIraBenePayloadObject,
  Beneficiary,
  BeneficiaryAddress,
  IraPlan,
} from '../../../types'
import {
  bulletedLastFour,
  getErrorMessage,
  removeLeadingZeros,
} from '../../../utils/formatter'
import { useIraBeneficiaries } from '../../../services/IraBeneficiariesProvider'
import { useHostServices } from '../../../HostServices'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import {
  GENERIC_ERROR_MESSAGE,
  RELATIONSHIP_OPTIONS,
  MAX_IRA_BENES,
} from '../../../utils/constants'
import { fireErrorTagging } from '../../../utils/tagging'

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: ${get.space('md')};
  margin-bottom: ${get.space('md')};
`

const AllocationWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${get.space('sm')};
  margin-bottom: ${get.space('sm')};
`
export const VerifyStep: React.FC<{
  stepTwoFormValues: StepTwoFormValues
  allocationStepIraValues: AllocationStepIraValues
  existingBenes: Beneficiary[] | undefined
  accountId: string
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
  setStepTwoFormErrorCode: React.Dispatch<React.SetStateAction<string>>
}> = ({
  stepTwoFormValues,
  allocationStepIraValues,
  existingBenes,
  setErrorMessage,
  setStepTwoFormErrorCode,
  accountId,
}) => {
  const { iraPlans, getIraBeneficiaries, isCommunityPropertyState } =
    useIraBeneficiaries()
  const { setOpen } = useModalContext()
  const [isChecked, setIsChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorRef, focusErrorRef] = useFocus()
  const [hasSignatureValidationError, setSignatureValidationError] =
    useState(false)

  const currentPlan =
    iraPlans && iraPlans.find((plan: IraPlan) => plan.planId === accountId)

  // istanbul ignore next
  const filteredIraBenes = existingBenes?.filter(
    bene =>
      bene.beneficiaryType?.toLowerCase() === stepTwoFormValues.beneficiaryType,
  )

  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    maskedSsn,
    relationshipToOwner,
    beneficiaryType,
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    sameAddress,
    identificationNumber,
    isMarried,
  } = stepTwoFormValues

  const { newBeneficiaryFundAllocation } = allocationStepIraValues
  const { createIraBeneficiary } = useBeneficiariesApi()
  const { hostData } = useHostServices()
  const { triggerSnackbar } = useTriggerSnackbar()
  const { setActiveStep } = useSteppedProcess()
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  // istanbul ignore next optional chaining
  const customerAddress = hostData?.afgCustomer?.data?.data?.addresses.find(
    (address: any) => address.type === 'PRIMARY',
  )

  const getCustomerAddress = (): BeneficiaryAddress => {
    if (sameAddress === 'Yes' && customerAddress) {
      // istanbul ignore next optional chaining
      return {
        addressLine1: customerAddress?.line1,
        addressLine2: customerAddress?.line2 || '',
        city: customerAddress?.city as string,
        state: customerAddress?.state as string,
        zipCode: customerAddress?.zipCode as string,
        country: 'US',
      }
    }

    return {
      addressLine1: addressLine1 as string,
      addressLine2: addressLine2 || '',
      city: city as string,
      state: state as string,
      zipCode: zipCode as string,
      country: 'US',
    }
  }

  const getBeneficiaryType = beneficiaryType
    ? `${beneficiaryType[0].toUpperCase()}${beneficiaryType.slice(1)}`
    : ''

  const getRelationshipToOwner = (): string => {
    if (isMarried && getBeneficiaryType === 'Primary') {
      return 'Spouse'
    }
    const relationshipToOwnerSelection = RELATIONSHIP_OPTIONS.find(
      el => el.value === relationshipToOwner,
    )
    return relationshipToOwnerSelection
      ? relationshipToOwnerSelection.label
      : ''
  }

  const getMaritalStatus = (): string => {
    if (isCommunityPropertyState) {
      if (isMarried) {
        return 'Married'
      }
      return 'Unmarried'
    }
    return ''
  }

  const newBeneficiaryPayload = (): AddIraBenePayloadObject => ({
    firstName: firstName as string,
    middleName,
    lastName: lastName as string,
    dateOfBirth: dayjs(dateOfBirth).format('YYYY-MM-DD').toString(),
    address: getCustomerAddress(),
    allocationPercentage:
      filteredIraBenes && filteredIraBenes.length > 0
        ? removeLeadingZeros(newBeneficiaryFundAllocation)
        : 100,
    relationshipToOwner: getRelationshipToOwner(),
    maritalStatus: getMaritalStatus(),
    ssn: identificationNumber as string,
    beneficiaryType: getBeneficiaryType,
  })

  const createPayloadObject = (): any => {
    const newBeneficiary = newBeneficiaryPayload()
    if (filteredIraBenes && filteredIraBenes.length > 0) {
      const allBenePayload = filteredIraBenes
        ? filteredIraBenes.map(bene => ({
            address: {
              addressLine1: bene.address.addressLine1 as string,
              addressLine2: bene.address.addressLine2 || '',
              city: bene.address.city as string,
              state: bene.address.state as string,
              zipCode: bene.address.zipCode as string,
              country: 'US',
            },
            beneficiaryId: bene.beneficiaryId,
            allocationPercentage: removeLeadingZeros(
              allocationStepIraValues[
                bene.beneficiaryId as keyof AllocationStepIraValues
              ],
            ),
            maritalStatus: getMaritalStatus(),
          }))
        : // istanbul ignore next // low value
          []
      return {
        beneficiaries: [newBeneficiary, ...allBenePayload],
      }
    }
    return { beneficiary: newBeneficiary }
  }

  const onSubmit = async (): Promise<void> => {
    if (isChecked) {
      setShowErrorMessage(false)
      setIsLoading(true)
      const payload = createPayloadObject()
      try {
        setErrorMessage('')
        setStepTwoFormErrorCode('')
        await createIraBeneficiary(accountId, payload)
        await getIraBeneficiaries()
        // istanbul ignore next
        triggerSnackbar({
          text: 'You updated your beneficiaries.',
          columnSize: 4,
          snackbarButtonText: 'Close',
          onClose: () =>
            requestAnimationFrame(() => {
              // This if condition bring focus on max bene warning message
              if (existingBenes?.length === MAX_IRA_BENES - 1) {
                document
                  .getElementById(`${accountId}-add-bene-success-focus`)
                  ?.getElementsByTagName('span')[0]
                  .focus()
              } else {
                document
                  .getElementById(`${accountId}-add-bene-success-focus`)
                  ?.focus()
              }
              document.getElementById(`${accountId}-heading`)?.focus()
            }),
        })
        // istanbul ignore next - tagging
        allytm.event('customEvent', {
          name: 'formSuccess',
          type: 'formValidationSuccess',
          attr: {
            formValues: {
              form: 'Submit IRA Beneficiary',
              values: [],
            },
          },
        })
      } catch (err: any) {
        const errorMessage = getErrorMessage(err)
        allytm.event(
          'customError',
          stepTwoBeneErrors[errorMessage]
            ? stepTwoBeneErrors[errorMessage].errorMessage
            : 'It’s us – we’re having a moment. We couldn’t add your beneficiary. We’ll be quick about it, so try again in a bit.',
          errorMessage,
        )
        if (
          errorMessage === 'USB_BUS_351' ||
          errorMessage === 'USB_BUS_357' ||
          errorMessage === 'USB_BUS_634' ||
          errorMessage === 'USB_BUS_633' ||
          errorMessage === 'USB_BUS_350'
        ) {
          setStepTwoFormErrorCode(errorMessage)
          // istanbul ignore next
          setActiveStep(0)
        } else {
          setShowErrorMessage(true)
          allytm.event(
            'customError',
            'We couldn’t process your request. Please contact Customer Service at 1-877-247-2559.',
          )
        }
      } finally {
        setIsLoading(false)
      }
    } else {
      setSignatureValidationError(true)
      fireErrorTagging({ verifyBeneficiary: 'Check this box to continue.' })
      requestAnimationFrame(focusErrorRef)
    }
  }

  useEffect(() => {
    allytm.event(
      'modalView',
      'AOS:Beneficiaries:Bank IRAs:Add Beneficiary:Verify Beneficiary',
      {
        treatAsPageview: true,
      },
    )
  }, [])

  return (
    <>
      {showErrorMessage && (
        <Alert
          variant="error"
          focusOnMount
          disableContainer
          body={GENERIC_ERROR_MESSAGE}
        />
      )}
      {currentPlan && (
        <Box mt="md" mb="md">
          <Label htmlFor="plan">Plan</Label>
          <Private>
            <TextBody tag="span" size="sm">
              {currentPlan.planType} IRA Plan{' '}
              {bulletedLastFour(currentPlan.planNumber)}
            </TextBody>
          </Private>
        </Box>
      )}

      <Box mb="sm">
        <Label htmlFor="beneficiaryName">Beneficiary Name</Label>
        <Private>
          <TextBody tag="span" size="sm">
            {`${firstName} ${middleName || ''} ${lastName}`}
          </TextBody>
        </Private>
      </Box>

      <Box mb="sm">
        <Label htmlFor="dateOfBirth">Date of Birth</Label>
        <Private>
          <TextBody tag="span" size="sm">
            {dateOfBirth}
          </TextBody>
        </Private>
      </Box>

      <Box mb="sm">
        <Label htmlFor="socialSecurityNumber">Social Security Number</Label>
        <Private>
          <TextBody tag="span" size="sm">
            {maskedSsn}
          </TextBody>
        </Private>
      </Box>
      <Box mb="sm">
        <Label htmlFor="relationshipToOwner">Relationship</Label>
        <Private>
          <TextBody tag="span" size="sm">
            {getRelationshipToOwner()}
          </TextBody>
        </Private>
      </Box>
      {beneficiaryType && beneficiaryType !== '' ? (
        <Box mb="sm">
          <Label htmlFor="beneficiaryType">Beneficiary Type</Label>
          <Private>
            <TextBody tag="span" size="sm">
              {`${beneficiaryType[0].toUpperCase()}${beneficiaryType.slice(1)}`}
            </TextBody>
          </Private>
        </Box>
      ) : (
        <Box mb="sm">
          <Label htmlFor="beneficiaryType">Beneficiary Type</Label>
          <Private>
            <TextBody tag="span" size="sm">
              Primary
            </TextBody>
          </Private>
        </Box>
      )}

      <Box mb="sm">
        <Label htmlFor="fundsAllocation">Funds Allocation</Label>
        {filteredIraBenes && filteredIraBenes.length > 0 ? (
          <AllocationWrapper>
            <Private>
              <TextBody tag="span" size="sm">
                {`${firstName} ${middleName || ''} ${lastName}`}
              </TextBody>
            </Private>
            <TextBody tag="span" size="sm">
              {`${removeLeadingZeros(
                allocationStepIraValues.newBeneficiaryFundAllocation,
              )}%`}
            </TextBody>
          </AllocationWrapper>
        ) : (
          <TextBody tag="span" size="sm">
            100%
          </TextBody>
        )}
        {filteredIraBenes &&
          filteredIraBenes.map((bene: Beneficiary) => (
            <AllocationWrapper key={bene.beneficiaryId}>
              <Private>
                <TextBody tag="div" size="sm">
                  {`${bene.firstName} ${
                    bene.middleName ? bene.middleName : ''
                  } ${bene.lastName}`}
                </TextBody>
              </Private>
              <TextBody tag="div" size="sm">
                {`${removeLeadingZeros(
                  allocationStepIraValues[bene.beneficiaryId],
                )}%`}
              </TextBody>
            </AllocationWrapper>
          ))}
      </Box>
      {hasSignatureValidationError && (
        <Alert
          variant="error"
          disableContainer
          body="Check this box to continue."
          ref={errorRef}
        />
      )}
      <Box mb="md" mr="md">
        <CheckboxStateful
          label="By checking this box, I sign and agree that the information I provided is true and accurately reflects my choices."
          name="authorizationCheckbox"
          onChange={({ target: { checked } }): void => {
            if (checked) {
              setSignatureValidationError(false)
            }
            setIsChecked(checked)
          }}
          data-testid="verify-request-checkbox"
        />
      </Box>

      <StyledButtonGroup>
        <Button
          allytmln="Submit Add IRA Beneficiary"
          text="Submit"
          onClick={onSubmit}
          loading={isLoading}
          data-testid="submit-continue-button"
        />
        <Button
          allytmln="Cancel Add Beneficiary"
          text="Cancel"
          onClick={
            // istanbul ignore next
            (): void => setOpen(false)
          }
          variant="link"
        />
      </StyledButtonGroup>
    </>
  )
}
